import {
    getPrimaryImage,
    getMediaImageUrl,
    hasFaceDetection,
    getOptimalCropArea,
    sortImages
} from '../../util/media';
import { Media as MediaType, MediaUseCase } from '../../types/media';
import { Avatar, AvatarProps } from '@mui/material';
import { useMemo } from 'react';

interface MediaProps extends AvatarProps {
    media?: MediaType;
    options?: { width?: number; height?: number; quality?: number };
    useCase?: MediaUseCase;
    fallback?: React.ReactNode;
    full?: boolean;
    responsive?: boolean;
    useFaceDetection?: boolean;
    /**
     * How the image fills the box. MUI's `Avatar` hard-codes `object-fit: cover` on
     * `.MuiAvatar-img`, which crops anything that is not square — fine for a face, wrong for a
     * logo, which has to be letterboxed whole. `cover` is the default so no existing caller
     * changes appearance.
     */
    fit?: 'cover' | 'contain';
}

export const Media = ({
    media,
    options,
    useCase,
    fallback,
    sx,
    full = false,
    responsive = true,
    useFaceDetection = false, // this is switch off for now
    fit = 'cover',
    ...avatarProps
}: MediaProps) => {
    if (!media || !media.images || media.images.length === 0) {
        return (
            <Avatar {...avatarProps} sx={sx}>
                {fallback}
            </Avatar>
        );
    }

    const hasFaces = useFaceDetection ? hasFaceDetection(media) : false;

    sx = {
        ...(full && { borderRadius: 0 }),
        // Applied here rather than on each variant below: every one of them renders an `Avatar`
        // and receives this same `sx`.
        ...(fit !== 'cover' && { '& .MuiAvatar-img': { objectFit: fit } }),
        ...sx
    };

    if (hasFaces) {
        return (
            <MediaWithFace
                media={media}
                options={options}
                fallback={fallback}
                sx={sx}
                responsive={responsive}
                {...avatarProps}
            />
        );
    }

    if (responsive && media?.images?.length > 1) {
        return (
            <ResponsiveMedia
                media={media}
                options={options}
                fallback={fallback}
                sx={sx}
                {...avatarProps}
            />
        );
    }

    // Fallback to primary image for single images or when responsive is disabled
    const primaryImage = getPrimaryImage(media);
    if (!primaryImage) {
        return <Avatar {...avatarProps}>{fallback}</Avatar>;
    }

    return (
        <Avatar
            src={getMediaImageUrl(primaryImage, options || {})}
            alt="Image"
            sx={sx}
            {...avatarProps}
        />
    );
};

export const ResponsiveMedia = ({ media, options, fallback, sx, ...avatarProps }: MediaProps) => {
    const responsiveData = useMemo(() => {
        if (!media || media.images.length <= 1) return null;

        // Sort images by area (largest first) for better srcset
        const sortedImages = sortImages(media.images, 'area', 'desc');

        // Create srcset with different image sizes
        const srcset = sortedImages
            .map(image => {
                const url = getMediaImageUrl(image, options || {});
                return `${url} ${image.width}w`;
            })
            .join(', ');

        // Get the largest image as default
        const defaultImage = sortedImages[0];
        const defaultSrc = getMediaImageUrl(defaultImage, options || {});

        // Calculate sizes attribute based on typical avatar usage
        const sizes = options?.width
            ? `${options.width}px`
            : '(max-width: 600px) 40px, (max-width: 960px) 56px, 80px';

        return {
            srcset,
            sizes,
            defaultSrc,
            alt: 'Responsive image'
        };
    }, [media, options]);

    if (!responsiveData) {
        return <Avatar {...avatarProps}>{fallback}</Avatar>;
    }

    return (
        <Avatar
            src={responsiveData.defaultSrc}
            srcSet={responsiveData.srcset}
            sizes={responsiveData.sizes}
            alt={responsiveData.alt}
            sx={sx}
            {...avatarProps}
        />
    );
};

export const MediaWithFace = ({
    media,
    options,
    fallback,
    sx,
    responsive = true,
    ...avatarProps
}: MediaProps) => {
    if (!media) {
        return <Avatar {...avatarProps}>{fallback}</Avatar>;
    }

    const primaryImage = getPrimaryImage(media);

    if (!primaryImage) {
        return <Avatar {...avatarProps}>{fallback}</Avatar>;
    }

    const targetWidth = options?.width || 40;
    const targetHeight = options?.height || 40;

    const cropArea = getOptimalCropArea(media, targetWidth, targetHeight);

    if (!cropArea) {
        // If no crop area, fall back to responsive or regular image
        if (responsive && media.images.length > 1) {
            return (
                <ResponsiveMedia
                    media={media}
                    options={options}
                    fallback={fallback}
                    sx={sx}
                    {...avatarProps}
                />
            );
        }

        return (
            <Avatar
                src={getMediaImageUrl(primaryImage, options || {})}
                alt="Image"
                sx={sx}
                {...avatarProps}
            />
        );
    }

    const { x, y, width, height } = cropArea;
    const cropCenterX = x + width / 2;
    const cropCenterY = y + height / 2;
    const objectPositionX = (cropCenterX / primaryImage.width) * 100;
    const objectPositionY = (cropCenterY / primaryImage.height) * 100;

    // For face-aware cropping, we still use the primary image but with crop positioning
    return (
        <Avatar
            src={getMediaImageUrl(primaryImage, options || {})}
            alt="Image"
            sx={{
                objectFit: 'cover',
                objectPosition: `${objectPositionX}% ${objectPositionY}%`,
                ...sx
            }}
            {...avatarProps}
        />
    );
};
